import { useSelector } from "react-redux";
import { isValidNumber } from "src/components/core/IntlTelInput/IntlTelInput";
import i18n from "src/locales";
import { isNursing } from "src/utils/pace/planTimeAdjustmentNursing";
import {
  CollapsedEnrollmentData,
  requestEnrollment,
} from "src/utils/api/enrollment";
import { trackEvent, updateUserContext } from "src/utils/api/tracker";
import { getLanguage } from "src/utils/meristemContext";
import { getWeightLossGoalData } from "src/utils/pace";
import { CoreReduxState } from "src/utils/redux/internal";
import { updateCheckoutState } from "src/utils/redux/slices/checkout";
import {
  EnrollmentErrors,
  PaymentEnrollmentFormState,
  updatePaymentEnrollmentFormState,
} from "src/utils/redux/slices/paymentEnrollmentForm";
import { genderConstantsMapping, sexConstants } from "src/utils/sexAndGender";
import { isHM, isMobile, isUpsellRoute } from "src/utils/userSegment";
import {
  showAccountInfoAccordion,
  isInAppMedUpgrade,
  acceptedNoomClinicalOffer,
  hasExistingAcccount,
  isHRTSurvey,
  isAgeEligibleForMht,
  isAgeEligibleForMed,
  isFlowEligibleForHRTProducts,
  shouldUsePostAuthCheckoutFlow,
} from "src/utils/userSegment/features";
import { isEmail, isName } from "src/utils/validate";
import { AGREEMENT_CONFIGS } from "src/checkout/components/payment/disclaimer/agreementConfig";
import { useCallbackRef, useOnce } from "./lifecycle";
import { useAppDispatch, useCheckout, useUserData } from "./redux";
import { getSurveyAnswers, useSurveyAnswers } from "./survey/answers";
import { useEffect } from "react";
import { calculateAge, format } from "@utils/datetime";
import { getFullName } from "src/utils/name";
import type { StatusEnumProto_Value } from "@noom/noom-contracts/noom_contracts/backend/privacy/user_policy_options";
import { getActiveUser } from "@noom/hive-auth";

export function useEnrollmentForm() {
  const checkout = useCheckout();
  const enrollmentForm = useSelector(
    (state: CoreReduxState) => state.paymentEnrollmentForm
  );
  const {
    enrollmentInfo,
    enrollmentErrors = {},
    showInvalidAgeModal,
    agreementValidationMessage,
  } = enrollmentForm;
  const hasEnrollmentErrors = Object.values(enrollmentErrors).some(Boolean);

  const dispatch = useAppDispatch();

  function updateEnrollmentInfo(
    newEnrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"],
    handleNameChangeMade = true
  ) {
    const nameChangeMade = handleNameChangeMade
      ? (newEnrollmentInfo.name || "") !== (enrollmentInfo.name || "")
      : enrollmentInfo.nameChangeMade;
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentInfo: {
          ...newEnrollmentInfo,
          nameChangeMade: enrollmentInfo.nameChangeMade || nameChangeMade,
        },
        enrollmentErrors: validateEnrollmentInfo(newEnrollmentInfo),
      })
    );
  }

  // TODO: See if there is a better way to seed this
  // Set default values and default errors for enrollment form
  const userData = useUserData();
  useOnce(() => {
    if (!enrollmentInfo.name && !enrollmentInfo.email) {
      const newEnrollmentInfo = {
        ...enrollmentInfo,
        name:
          userData.name ||
          userData.purchaseSurveyAnswers?.firstName ||
          userData.personaSurveyUS?.firstName,
        email: (getActiveUser()?.email || userData.email || "").toLowerCase(),
      };

      updateEnrollmentInfo(newEnrollmentInfo);
    }
  });

  useEffect(() => {
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentErrors: validateEnrollmentInfo(enrollmentInfo),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setField(
    values: Omit<
      Partial<PaymentEnrollmentFormState["enrollmentInfo"]>,
      "termAgreement"
    >
  ) {
    const newEnrollmentInfo = {
      ...enrollmentInfo,
      ...values,
    };

    if ("dateOfBirth" in values) {
      const oldDob = enrollmentInfo.dateOfBirth || "";
      const newDob = values.dateOfBirth || "";

      // If user deleted a character, automatically remove trailing slash /
      if (newDob.length < oldDob.length && newDob[newDob.length - 1] === "/") {
        newEnrollmentInfo.dateOfBirth = newDob.slice(0, newDob.length - 1);
      }
      // If user added a character
      else if (newDob.length > oldDob.length) {
        // Remove non numeric characters and ensure we only have 8 numbers max
        const rawDob = newDob.replace(/[^0-9]/g, "").slice(0, 8);
        const parts = [];
        if (rawDob.length) {
          // month
          parts.push(rawDob.slice(0, 2));
        }
        if (rawDob.length >= 2) {
          // day
          parts.push(rawDob.slice(2, 4));
        }
        if (rawDob.length >= 4) {
          // year
          parts.push(rawDob.slice(4));
        }
        // Automatically add a slash between month and day, day and year to ensure the format dd/mm/yyyy
        newEnrollmentInfo.dateOfBirth = parts.join("/");
      }
    }

    ["password", "confirmPassword", "email"].forEach((field) => {
      if (field in values) {
        newEnrollmentInfo[field] = values[field].replace(/\s/g, "");
        if (field === "email") {
          newEnrollmentInfo[field] = newEnrollmentInfo[field].toLowerCase();
        }
      }
    });

    if ("firstName" in values || "lastName" in values) {
      // Keep the name value in sync even when hidden since
      // we still need to send it to the backend
      newEnrollmentInfo.name = getFullName(
        newEnrollmentInfo.firstName,
        newEnrollmentInfo.lastName
      );
    }

    updateEnrollmentInfo(newEnrollmentInfo);
    dispatch(updateCheckoutState({ userStartedEnteringPaymentInfo: true }));
  }

  function hasVisibleError(field?: keyof EnrollmentErrors) {
    const { showEnrollmentErrors } = checkout;

    const confirmPasswordRealTimeValidation =
      field === "confirmPassword" &&
      enrollmentInfo.password.length > 0 &&
      enrollmentInfo.password.length <= enrollmentInfo.confirmPassword.length;

    if (!showEnrollmentErrors && !confirmPasswordRealTimeValidation) {
      return false;
    }

    return field ? !!enrollmentErrors[field] : hasEnrollmentErrors;
  }

  function setAgreementState(key: string, checked: boolean) {
    const newEnrollmentInfo = {
      ...enrollmentInfo,
      [key]: checked,
    };

    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentInfo: newEnrollmentInfo,
      })
    );

    validateAgreements(newEnrollmentInfo);
  }

  function validateAgreements(
    enrollmentInfoToValidate?: PaymentEnrollmentFormState["enrollmentInfo"]
  ) {
    const infoToValidate = enrollmentInfoToValidate || enrollmentInfo;

    const uncheckedRequired = AGREEMENT_CONFIGS.filter(
      (config) =>
        config.condition() && config.required && !infoToValidate[config.key]
    );

    if (uncheckedRequired.length > 0) {
      const errorMessage =
        uncheckedRequired.length === 1
          ? i18n.t("payment:terms:error")
          : i18n.t("payment:terms:errorPlural");

      dispatch(
        updatePaymentEnrollmentFormState({
          agreementValidationMessage: errorMessage,
        })
      );

      const errors = {};
      uncheckedRequired.forEach((config) => {
        errors[config.key] = true;
      });

      return errors;
    }

    dispatch(
      updatePaymentEnrollmentFormState({
        agreementValidationMessage: null,
      })
    );

    return undefined;
  }

  function validateForm() {
    if (
      (showAccountInfoAccordion() || isInAppMedUpgrade() || isHRTSurvey()) &&
      hasEnrollmentErrors
    ) {
      return enrollmentErrors;
    }
    return undefined;
  }

  function updateUserContextBirthDate() {
    if (hasEnrollmentField("dateOfBirth")) {
      const { dateOfBirth } = enrollmentInfo;
      updateUserContext({
        dateOfBirth,
      });
    }
  }

  return {
    enrollmentInfo,
    setField: useCallbackRef(setField),
    updateEnrollmentInfo: useCallbackRef(updateEnrollmentInfo),

    validateForm: useCallbackRef(validateForm),

    enrollmentErrors,
    hasEnrollmentErrors,
    hasVisibleError: useCallbackRef(hasVisibleError),

    agreementValidationMessage,
    setAgreementState: useCallbackRef(setAgreementState),
    validateAgreements: useCallbackRef(validateAgreements),

    showInvalidAgeModal,
    updateUserContextBirthDate,
  };
}

export function useEnroll() {
  const { enrollmentInfo } = useEnrollmentForm();
  const surveyAnswers = useSurveyAnswers();

  async function enroll(curriculum?: string) {
    // Track survey answer fields that do not exist and populate with default values
    const missingFields = [];
    let { gender, heightCm, weightKg, ageRange, liabilityWaiver } =
      surveyAnswers;
    const {
      email,
      name,
      password,
      phoneNumber,
      atHomeLabsConsent,
      medicareAttestationConsent,
    } = enrollmentInfo;

    let genderMapping: string;
    if (!gender?.[0]) {
      missingFields.push("gender");
      genderMapping = genderConstantsMapping.UNKNOWN;
    } else {
      genderMapping = genderConstantsMapping[gender[0]];
    }
    if (!heightCm) {
      missingFields.push("height");
      heightCm = 170;
    }
    if (!weightKg) {
      missingFields.push("weight");
      weightKg = 65;
    }
    if (!ageRange) {
      missingFields.push("age");
      ageRange = ["40s"];
    }
    if (!name) {
      missingFields.push("name");
    }
    // Fire off event only if missingFields is empty
    if (missingFields.length && !isHM()) {
      trackEvent("ProfileFieldMissing", {
        missingFields,
      });
    }

    // Pass in data needed for CS
    const userProfileData: CollapsedEnrollmentData = {
      email,
      name,
      password,
      phoneNumber,
      language: getLanguage(),
      gender: genderMapping,
      heightInCm: heightCm,
      weightInKg: weightKg,
      age: parseInt(ageRange[0], 10),
      isNursing: isNursing(surveyAnswers),
      ...(liabilityWaiver &&
        liabilityWaiver[0] === "agree" && {
          liabilityWaiverConsent: liabilityWaiver[0].toUpperCase(),
        }),
      ...(atHomeLabsConsent && {
        ashAtHomeTestConsent: "AGREE" as StatusEnumProto_Value,
      }),
      ...(medicareAttestationConsent && {
        enrolledInMedicare: "yes",
        medicareConsent: "agree",
      }),
    };

    if (!isHM()) {
      const weightLossData = getWeightLossGoalData(surveyAnswers);
      if (weightLossData) {
        (userProfileData as any).weightLossData = weightLossData;
      }
    }

    return requestEnrollment({
      userProfileData,
      // Filter HW curriculum to maintain parity with prior impl
      // TODO: Check if the API is fine with this being passed
      curriculum: curriculum === "HW" ? undefined : curriculum,
    });
  }

  return {
    enroll: useCallbackRef(enroll),
  };
}

export function isMedIneligibleDateOfBirth(dateOfBirth: string) {
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) {
    return false;
  }
  const age = calculateAge(dob);
  const failHRTValidation =
    isFlowEligibleForHRTProducts() && !isAgeEligibleForMht(age);
  const failMedValidation =
    !isFlowEligibleForHRTProducts() && !isAgeEligibleForMed(age);
  return failHRTValidation || failMedValidation;
}

function getDateOfBirthError(dateOfBirth: string) {
  const dob = new Date(dateOfBirth);

  if (!dateOfBirth) {
    return i18n.t(
      "enrollmentForm:inputFields:dateOfBirth:errorMessages:invalid"
    );
  }

  // by checking the inputted {String} dob against the formatted version of {Date} dob
  // we can verify that the input was correctly formatted and the date exists
  if (dateOfBirth !== format("{MM}/{DD}/{YYYY}", dob)) {
    return i18n.t(
      "enrollmentForm:inputFields:dateOfBirth:errorMessages:invalidFormat"
    );
  }

  const today = new Date();
  if (dob > today) {
    return i18n.t(
      "enrollmentForm:inputFields:dateOfBirth:errorMessages:future"
    );
  }
  if (isMedIneligibleDateOfBirth(dateOfBirth)) {
    const productKey = isFlowEligibleForHRTProducts() ? "hrt" : "med";
    return i18n.t(`payment:ineligibleAgeModal:${productKey}:text1`);
  }

  return "";
}

function getInvalidNameCharacters(name: string) {
  const allowedPattern = /[a-zA-ZÀ-ÖØ-öø-ÿ0-9\s\-'']/g;

  const invalidChars = [...new Set(name.replace(allowedPattern, ""))];

  return invalidChars.join("");
}

function getFirstOrLastNameFieldErrors(
  key: "firstName" | "lastName",
  fieldValue: string
) {
  if (!isName(fieldValue)) {
    return i18n.t(`enrollmentForm:inputFields:${key}:errorMessages:required`);
  }

  if (fieldValue.length < 2) {
    return i18n.t(
      "enrollmentForm:inputFields:firstOrLastName:errorMessages:tooShort"
    );
  }

  if (getInvalidNameCharacters(fieldValue)) {
    return i18n.t(
      "enrollmentForm:inputFields:firstOrLastName:errorMessages:invalidCharacters",
      {
        invalidCharacters: getInvalidNameCharacters(fieldValue),
      }
    );
  }

  return "";
}

function validateEnrollmentInfo(
  enrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"]
) {
  const {
    email,
    password,
    confirmPassword,
    name,
    firstName,
    lastName,
    phoneNumber,
    phoneNumberCountryCode,
    dateOfBirth,
    biologicalSex,
  } = enrollmentInfo;
  const enrollmentErrors: EnrollmentErrors = {
    name: false,
    email: false,
    password: false,
    confirmPassword: false,
    phoneNumber: false,
    biologicalSex: false,
  };

  if (hasEnrollmentField("name")) {
    if (!isName(name)) {
      enrollmentErrors.name = i18n.t(
        "enrollmentForm:inputFields:name:errorMessages:required"
      );
    }
  }

  if (hasEnrollmentField("firstName")) {
    enrollmentErrors.firstName = getFirstOrLastNameFieldErrors(
      "firstName",
      firstName
    );
  }
  if (hasEnrollmentField("lastName")) {
    enrollmentErrors.lastName = getFirstOrLastNameFieldErrors(
      "lastName",
      lastName
    );
  }
  if (hasEnrollmentField("dateOfBirth")) {
    enrollmentErrors.dateOfBirth = getDateOfBirthError(dateOfBirth);
  }

  if (hasEnrollmentField("biologicalSex") && !biologicalSex) {
    enrollmentErrors.biologicalSex = i18n.t(
      "enrollmentForm:inputFields:biologicalSex:errorMessages:required"
    );
  }

  if (hasEnrollmentField("email") && !isEmail(email)) {
    enrollmentErrors.email = i18n.t(
      "enrollmentForm:inputFields:email:errorMessages:invalid"
    );
  }

  if (hasEnrollmentField("password")) {
    if (password?.length < 1) {
      enrollmentErrors.password = i18n.t(
        "enrollmentForm:inputFields:password:errorMessages:required"
      );
    } else if (password?.length < 4) {
      enrollmentErrors.password = i18n.t(
        "enrollmentForm:inputFields:password:errorMessages:minLength"
      );
    }
    if (hasEnrollmentField("confirmPassword") && password !== confirmPassword) {
      enrollmentErrors.confirmPassword = i18n.t(
        "enrollmentForm:inputFields:confirmPassword:errorMessages:passwordsNotMatching"
      );
    }
  }
  if (
    hasEnrollmentField("phoneNumber") &&
    !isValidNumber(phoneNumber, phoneNumberCountryCode)
  ) {
    enrollmentErrors.phoneNumber = i18n.t(
      "enrollmentForm:inputFields:phoneNumber:errorMessages:invalid"
    );
  }
  return enrollmentErrors;
}

export type InputFieldKey =
  | "firstName"
  | "lastName"
  | "name"
  | "email"
  | "dateOfBirth"
  | "password"
  | "confirmPassword"
  | "smsConsent"
  | "phoneNumber"
  | "phoneDisclaimer"
  | "biologicalSex";

/**
 * Determines if the biological sex field should be shown at checkout.
 * Shows the field only when the user's prior sex answer is intersex,
 * preferNotToSay, or missing (for clinical SKUs).
 */
export function shouldShowBiologicalSexField(): boolean {
  if (!acceptedNoomClinicalOffer()) {
    return false;
  }

  const { sex } = getSurveyAnswers();

  // Show field if sex is intersex, preferNotToSay, or not provided
  return (
    !sex?.length ||
    sex?.includes(sexConstants.intersex) ||
    sex?.includes(sexConstants.preferNotToSay)
  );
}

export function getEnrollmentFormFields(): Array<Array<InputFieldKey>> {
  const includeBiologicalSex = shouldShowBiologicalSexField();
  const isPostAuthCheckoutFlow = shouldUsePostAuthCheckoutFlow();

  if (isHRTSurvey()) {
    return [
      ["firstName", "lastName"],
      includeBiologicalSex && ["biologicalSex"],
      ["dateOfBirth", "phoneNumber"],
      ["smsConsent"],
    ].filter(Boolean) as Array<Array<InputFieldKey>>;
  }

  if (isInAppMedUpgrade()) {
    return [
      ["firstName", "lastName"],
      includeBiologicalSex && ["biologicalSex"],
      isPostAuthCheckoutFlow ? ["dateOfBirth", "phoneNumber"] : ["phoneNumber"],
    ].filter(Boolean) as Array<Array<InputFieldKey>>;
  }

  if (acceptedNoomClinicalOffer()) {
    return [
      ["firstName", "lastName"],
      !isPostAuthCheckoutFlow && ["email"],
      !isPostAuthCheckoutFlow &&
        !hasExistingAcccount() && ["password", "confirmPassword"],
      includeBiologicalSex && ["biologicalSex"],
      ["dateOfBirth", "phoneNumber"],
      ["smsConsent"],
    ].filter(Boolean) as Array<Array<InputFieldKey>>;
  }

  return [
    ["name"],
    !(isUpsellRoute() && !!getActiveUser()?.accessCode) && ["email"],
    !hasExistingAcccount() && ["password", "confirmPassword"],
    !isMobile() && ["phoneNumber", "phoneDisclaimer"],
  ].filter(Boolean) as Array<Array<InputFieldKey>>;
}

function hasEnrollmentField(fieldKey: InputFieldKey) {
  return getEnrollmentFormFields().flat().includes(fieldKey);
}
